<template>
  <div>
    <v-card v-if="!loadingStalled" :loading="loading" outlined>
      <v-card-title class="subtitle-2"> Performance Metrics </v-card-title>
      <v-card-subtitle v-if="from && to" class="caption font-italic">
        (based on date range {{ formatPerformanceRangeFrom(from) }} till
        {{ formatPerformanceRangeTo(to, updatedAt) }})
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-container fluid>
        <v-row align-content="space-around" justify="space-around">
          <v-col
            cols="auto"
            v-for="detail in details"
            :key="detail.measurement"
          >
            <div
              v-if="detail.measurement === 'System Pump Run time'"
              class="text-center"
            >
              <p class="text-uppercase caption mb-0">
                {{ detail.measurement }}
              </p>
              <p>
                <span class="font-weight-bold">{{ detail.day }}</span> day(s)
                <span class="font-weight-bold">{{ detail.hour }}</span> hr(s)
              </p>
            </div>
            <div
              v-else-if="detail.measurement === 'Under Service'"
              class="text-center"
            >
              <p class="text-uppercase caption mb-0">
                {{ detail.measurement }}
              </p>
              <p>
                <span class="font-weight-bold">{{ detail.openComplaints }}</span
                >/
                <span class="font-weight-bold">{{
                  detail.totalComplaints
                }}</span>
                ticket(s),
                <span class="font-weight-bold">{{ detail.value }}</span>
                {{ detail.unit }}
              </p>
            </div>
            <div class="text-center" v-else>
              <p class="text-uppercase caption mb-0">
                {{ detail.measurement }}
              </p>
              <p v-if="detail.value !== null">
                <span class="font-weight-bold">{{ detail.value }}</span>
                {{ detail.unit }}
              </p>
              <p v-else>
                <span class="font-weight-bold"> N/A </span>
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <div v-if="deviceType === 'ecotron'">
        <v-divider></v-divider>
        <p class="caption font-italic font-weight-light mb-0">
          * represents estimated values, rest are calculated from the data
        </p>
      </div>
    </v-card>
  </div>
</template>

<script>
import { nodeRestApi } from '../plugins/api';
import {
  dateFormat24hr,
  formatPerformanceRangeFrom,
  formatPerformanceRangeTo,
  getISTDayUtcRange
} from '../plugins/date';

export default {
  name: 'performance-metrics',
  props: {
    deviceType: {
      type: String,
      required: true
    },
    deviceId: {
      type: String,
      required: true
    },
    from: {
      type: String,
      required: true
    },
    to: {
      type: String,
      required: true
    },
    updatedAt: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      performanceMetricsRaw: {},
      loading: false,
      loadingStalled: false
    };
  },
  computed: {
    details() {
      if (this.deviceType === 'ecotron') {
        return [
          {
            measurement: 'Energy Produced',
            ...this.performanceMetricsRaw.energyProduced
          },
          {
            measurement: 'Water Pumped*',
            ...this.performanceMetricsRaw.waterPumped
          },
          {
            measurement: 'System Pump Run time',
            ...this.performanceMetricsRaw.systemPumpRunTime
          },
          {
            measurement: 'Monitoring Uptime',
            ...this.performanceMetricsRaw.monitoringUptime
          },
          {
            measurement: 'Carbon Offset*',
            ...this.performanceMetricsRaw.carbonOffset
          },
          {
            measurement: 'Days Pumping was done',
            ...this.performanceMetricsRaw.pumpingDays
          },
          {
            measurement: 'Under Service',
            value: 0,
            unit: 'days',
            ...this.performanceMetricsRaw.complaints
          }
        ];
      } else if (this.deviceType === 'ecofrost') {
        return [
          {
            measurement: 'Solar Consumption',
            ...this.performanceMetricsRaw.solarConsumption
          },
          {
            measurement: 'Grid Consumption',
            ...this.performanceMetricsRaw.gridConsumption
          },
          {
            measurement: 'Monitoring Downtime',
            ...this.performanceMetricsRaw.monitoringDownTime
          },
          {
            measurement: 'Door Opens',
            ...this.performanceMetricsRaw.doorOpen
          },
          {
            measurement: 'Battery Voltage Trip',
            ...this.performanceMetricsRaw.batteryTrip
          },
          {
            measurement: 'LP trip',
            ...this.performanceMetricsRaw.LPTrip
          },
          {
            measurement: 'HP Trip',
            ...this.performanceMetricsRaw.HPTrip
          },
          {
            measurement: 'Min. Superheat Temperature',
            ...this.performanceMetricsRaw.minSuperHeatTemperature
          },
          {
            measurement: 'Under Service',
            value: 0,
            unit: 'days',
            ...this.performanceMetricsRaw.complaints
          }
        ];
      } else {
        return [];
      }
    }
  },
  methods: {
    dateFormat24hr,
    formatPerformanceRangeFrom,
    formatPerformanceRangeTo,
    getPerformancePayload() {
      if (this.deviceType === 'ecofrost') {
        return getISTDayUtcRange(this.from, this.to);
      }

      return {
        from: this.from,
        to: this.to
      };
    },
    async updatePMetrics() {
      try {
        this.loadingStalled = false;
        // set loading on
        this.loading = true;
        // getting performance metrics
        const response = await nodeRestApi.post(
          `${this.deviceType}/performance-metrics/${this.deviceId}`,
          this.getPerformancePayload()
        );
        this.performanceMetricsRaw = response.data;
        // set loading off
        this.loading = false;
      } catch (error) {
        // set loading off
        this.loading = false;
        // loading has stalled
        this.loadingStalled = true;
        if (error.isAxiosError) {
          this.$store.commit('setError', error.response.data.error.message);
        } else {
          this.$store.commit('setError', error.response.data.error.message);
        }
      }
    }
  },
  async created() {
    try {
      this.loadingStalled = false;
      // set loading on
      this.loading = true;
      // getting performance metrics
      const response = await nodeRestApi.post(
        `${this.deviceType}/performance-metrics/${this.deviceId}`,
        this.getPerformancePayload()
      );
      this.performanceMetricsRaw = response.data;
      if (this.deviceType === 'ecotron') {
        this.$emit('pumpPower', response.data.pumpPower);
      }
      // set loading off
      this.loading = false;
    } catch (error) {
      // console.log(error.response.data.error)
      // set loading off
      this.loading = false;
      // loading has stalled
      this.loadingStalled = true;
      if (error.isAxiosError) {
        this.$store.commit('setError', error.response.data.error.message);
      } else {
        this.$store.commit('setError', error.response.data.error);
      }
    }
  }
};
</script>
